/**
 * Group decision rules by their `output` — visually-equivalent outcomes share
 * a single handle on the gateway. `GatewayNode` uses `groups` to render one
 * Handle per output; the process-flow builder uses `ruleIdToRepresentative`
 * to anchor every edge on the shared handle regardless of which specific rule
 * an input references. Keeping the mapping in one place prevents drift.
 */ export function groupRulesByOutput(rules) {
    const groups = [];
    const outputFirstRule = new Map();
    const ruleIdToRepresentative = new Map();
    const ruleIdToOutput = new Map();
    for (const rule of rules){
        if (!rule._id) continue;
        const output = rule.output ?? "";
        let rep = outputFirstRule.get(output);
        if (!rep) {
            rep = rule._id;
            outputFirstRule.set(output, rep);
            groups.push({
                output,
                firstRuleId: rep
            });
        }
        ruleIdToRepresentative.set(rule._id, rep);
        if (rule.output) ruleIdToOutput.set(rule._id, rule.output);
    }
    return {
        groups,
        ruleIdToRepresentative,
        ruleIdToOutput
    };
}
export function getDecisionTableColumns(rules) {
    const seen = new Set();
    const columns = [];
    for (const rule of rules){
        if (!rule.inputs) continue;
        for (const { column } of rule.inputs){
            if (!seen.has(column)) {
                seen.add(column);
                columns.push(column);
            }
        }
    }
    return columns;
}
/** Read a rule's value for a given column from the ordered inputs array. */ export function getRuleInputValue(inputs, column) {
    return inputs?.find((input)=>input.column === column)?.value;
}
/** A blank condition cell for every column — the inputs a fresh rule starts with. */ export function buildEmptyRuleInputs(columns) {
    return columns.map((column)=>({
            column,
            value: ""
        }));
}
/**
 * Move the rule identified by `activeId` to where `overId` currently sits,
 * preserving every other rule's relative order. Backs the editor's
 * drag-to-reorder. Object references are preserved, so each rule keeps its
 * `_id`/`successors`. Pure, so it can be unit-tested without a DnD harness.
 */ export function reorderRulesByClientId(rules, activeId, overId) {
    const oldIndex = rules.findIndex((rule)=>rule.clientId === activeId);
    const newIndex = rules.findIndex((rule)=>rule.clientId === overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return rules.slice();
    }
    const next = rules.slice();
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);
    return next;
}
/**
 * Force every rule in an output group to share the same `successors`. The
 * gateway renders one handle per unique `output`, so divergent successors
 * across siblings would silently break the single-line-per-output invariant.
 *
 * Canonical selection: first rule whose successors carry any populated
 * state; otherwise the first rule in the group.
 */ export function reconcileSharedOutputSuccessors(rules) {
    const hasMeaningfulSuccessors = (s)=>{
        if (!s) return false;
        if (s.successorStatus && s.successorStatus.trim() !== "") return true;
        return (s.persons?.length ?? 0) > 0 || (s.orgFunctions?.length ?? 0) > 0 || (s.systems?.length ?? 0) > 0 || (s.externalContacts?.length ?? 0) > 0;
    };
    const key = (r)=>r.output?.trim() ?? "";
    const canonicalByOutput = new Map();
    for (const rule of rules){
        const k = key(rule);
        const current = canonicalByOutput.get(k);
        if (!current) {
            canonicalByOutput.set(k, rule);
        } else if (!hasMeaningfulSuccessors(current.successors) && hasMeaningfulSuccessors(rule.successors)) {
            canonicalByOutput.set(k, rule);
        }
    }
    return rules.map((rule)=>{
        const canonical = canonicalByOutput.get(key(rule));
        if (!canonical?.successors) return rule;
        return {
            ...rule,
            successors: {
                ...canonical.successors
            }
        };
    });
}

//# sourceMappingURL=decisionTableColumns.js.map